/**
 * «Фінанси» — управлінський облік без налаштування (ч.1364, рішення власника: «функціонально,
 * але без ускладнення — наш користувач малий і середній бізнес»).
 *
 * Чотири відповіді за період: скільки заробив (прибуток за категоріями), куди пішли гроші і
 * скільки лишилось (рух за статтями, залишки), хто кому винен (борги), і план виручки на
 * місяць поруч із фактом. Кожна цифра «провалюється» у документи. Поруч із кожним числом —
 * «проти попереднього періоду». Нічого налаштовувати не треба: категорії виводяться з
 * проводок і документів.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { finance, monthBounds, type FinLine, type FinLines, type FinMetric, type FinSummary } from '@/api/finance';
import { usePermissions } from '@/auth/usePermissions';
import DatePopover from '@/components/DatePopover';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { useStickyState } from '@/hooks/useStickyFilters';
import BodyPortal from '@/shared/components/BodyPortal';
import { docRoute } from '@/shared/components/RelatedDocs';
import TchTable from '@/shared/components/TchTable';
import { MONTHS_UK } from '@/shared/components/PeriodFilter';
import { debtReportLinks } from '@/pages/reportLanding';
import { docTableName } from '@/utils/moduleNames';
import { extractApiError } from '@/utils/extractApiError';

type Preset = 'month' | 'quarter' | 'year' | 'custom';

const fmt = (v: number) => v.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmt0 = (v: number) => v.toLocaleString('uk-UA', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const pad2 = (n: number) => String(n).padStart(2, '0');
const quarterOf = (m: number) => Math.floor((m - 1) / 3) + 1;

function quarterBounds(year: number, q: number) {
  const first = (q - 1) * 3 + 1;
  return { period_start: `${year}-${pad2(first)}-01`, period_end: monthBounds(year, first + 2).period_end };
}

/** Зміна проти попереднього періоду: стрілка і відсоток (або «нове», якщо раніше було 0). */
export function Delta({ now, prev, invert = false }: { now: number; prev: number; invert?: boolean }) {
  if (now === prev) return <span style={{ color: 'var(--muted)', fontSize: '0.8rem' }}>без змін</span>;
  if (!prev) return <span style={{ color: 'var(--muted)', fontSize: '0.8rem' }}>раніше 0</span>;
  const pct = Math.round(((now - prev) / Math.abs(prev)) * 100);
  const better = invert ? pct < 0 : pct > 0;
  return (
    <span style={{ fontSize: '0.8rem', fontWeight: 600,
      color: better ? 'var(--badge-green-fg)' : 'var(--badge-red-fg)' }}>
      {pct > 0 ? '▲' : '▼'} {Math.abs(pct)}%
    </span>
  );
}

function Kpi({ label, value, sub, tone }: { label: string; value: string; sub?: React.ReactNode;
  tone?: 'good' | 'bad' }) {
  return (
    <div style={{ padding: '0.75rem 0.9rem', background: 'var(--bg-soft-2)', borderRadius: 'var(--radius)' }}>
      <div style={{ fontSize: '0.8rem', color: 'var(--muted)' }}>{label}</div>
      <div style={{ fontSize: '1.35rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums',
        color: tone === 'bad' ? 'var(--badge-red-fg)' : tone === 'good' ? 'var(--badge-green-fg)' : undefined }}>
        {value}
      </div>
      {sub && <div style={{ fontSize: '0.8rem', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

interface DrillTarget { metric: FinMetric; keys: string; title: string }

function DrillModal({ target, scope, onClose }: { target: DrillTarget;
  scope: { period_start: string; period_end: string; organization_id?: string }; onClose: () => void }) {
  const q = useQuery({
    queryKey: ['finance-drill', scope, target.metric, target.keys],
    queryFn: () => finance.drill({ ...scope, metric: target.metric, keys: target.keys }),
  });
  return (
    <BodyPortal>
      <div style={{ position: 'fixed', inset: 0, zIndex: 1100, background: 'var(--overlay)', display: 'flex',
        alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
        <div className="card" role="dialog" aria-label={`Документи: ${target.title}`}
          style={{ width: 760, maxWidth: '94vw', maxHeight: '78vh', overflowY: 'auto', margin: 0 }}
          onClick={(e) => e.stopPropagation()}>
          <div className="toolbar" style={{ marginBottom: '0.5rem' }}>
            <h3 style={{ margin: 0 }}>{target.title}</h3>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              {q.data && <span style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
                {q.data.count} док. · {fmt(q.data.total)} грн</span>}
              <button type="button" onClick={onClose}>закрити</button>
            </div>
          </div>
          {q.isLoading && <div style={{ color: 'var(--muted)' }}>Завантаження…</div>}
          {q.isError && <div className="error">{extractApiError(q.error)}</div>}
          {q.data && q.data.items.length === 0 && <div style={{ color: 'var(--muted)' }}>Документів немає.</div>}
          {q.data && q.data.items.length > 0 && (
            <TchTable>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>Документ</th>
                  <th style={{ width: 130 }}>№</th>
                  <th style={{ width: 100 }}>Дата</th>
                  <th style={{ textAlign: 'left' }}>Контрагент</th>
                  <th style={{ width: 130, textAlign: 'right' }}>Сума</th>
                </tr>
              </thead>
              <tbody>
                {q.data.items.map((it) => {
                  const route = docRoute(it.document_type, it.document_id);
                  const name = docTableName(it.document_type);
                  return (
                    <tr key={`${it.document_type}:${it.document_id}`}>
                      <td>{route ? <Link to={route}>{name}</Link> : name}</td>
                      <td>{it.number ?? '—'}</td>
                      <td>{it.date ? new Date(it.date).toLocaleDateString('uk-UA') : '—'}</td>
                      <td>{it.party ?? ''}</td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt(it.amount)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </TchTable>
          )}
          {q.data?.truncated && <div style={{ color: 'var(--muted)', fontSize: '0.85rem', marginTop: 6 }}>
            Показано перші {q.data.items.length} найбільших документів.</div>}
        </div>
      </div>
    </BodyPortal>
  );
}

function LinesTable({ title, block, metric, invert, onDrill, emptyText }: {
  title: string; block: FinLines; metric: FinMetric; invert?: boolean;
  onDrill: (t: DrillTarget) => void; emptyText: string;
}) {
  return (
    <>
      <tr style={{ background: 'var(--bg-soft-2)', fontWeight: 600 }}>
        <td>{title}</td>
        <td style={{ textAlign: 'right' }}>{fmt(block.total)}</td>
        <td style={{ textAlign: 'right', color: 'var(--muted)' }}>{fmt(block.prev)}</td>
        <td><Delta now={block.total} prev={block.prev} invert={invert} /></td>
      </tr>
      {block.lines.length === 0 && (
        <tr><td colSpan={4} style={{ color: 'var(--muted)', paddingLeft: '1.5rem' }}>{emptyText}</td></tr>
      )}
      {block.lines.map((l: FinLine) => (
        <tr key={l.key}>
          <td style={{ paddingLeft: '1.5rem' }}>
            <button type="button" className="btn-link" onClick={() => onDrill({ metric, keys: l.key, title: l.name })}
              title="Показати документи">{l.name}</button>
          </td>
          <td style={{ textAlign: 'right' }}>{fmt(l.amount)}</td>
          <td style={{ textAlign: 'right', color: 'var(--muted)' }}>{fmt(l.prev)}</td>
          <td><Delta now={l.amount} prev={l.prev} invert={invert} /></td>
        </tr>
      ))}
    </>
  );
}

function PlanEditor({ orgId, year, month, current, onSaved }: { orgId: string; year: number; month: number;
  current: number | null; onSaved: () => void }) {
  const [value, setValue] = useState(current != null ? String(current) : '');
  const [rest, setRest] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => finance.setRevenuePlan({ organization_id: orgId, year, month,
      amount: value.trim() === '' ? null : Number(value), apply_to: rest ? 'rest_of_year' : 'month' }),
    onSuccess: () => { setErr(null); onSaved(); },
    onError: (e) => setErr(extractApiError(e)),
  });
  return (
    <form onSubmit={(e) => { e.preventDefault(); save.mutate(); }}
      style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginTop: 4 }}>
      <label htmlFor="fin-plan" style={{ fontSize: '0.8rem' }}>План виручки на {(MONTHS_UK[month] ?? '').toLowerCase()}</label>
      <input id="fin-plan" type="number" min={0} step="0.01" value={value} style={{ width: 150 }}
        onChange={(e) => setValue(e.target.value)} placeholder="сума, грн" />
      <label style={{ fontSize: '0.8rem', display: 'inline-flex', gap: 4, alignItems: 'center' }}>
        <input type="checkbox" checked={rest} onChange={(e) => setRest(e.target.checked)} /> і до кінця року
      </label>
      <button type="submit" className="primary" disabled={save.isPending}>зберегти план</button>
      {err && <span className="error">{err}</span>}
    </form>
  );
}

function YearPlanModal({ orgId, year, onClose }: { orgId: string; year: number; onClose: () => void }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['finance-plan', orgId, year], queryFn: () => finance.revenuePlan(orgId, year) });
  const [draft, setDraft] = useState<Record<number, string>>({});
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    if (q.data) setDraft(Object.fromEntries(q.data.months.map((m) => [m.month, m.amount != null ? String(m.amount) : ''])));
  }, [q.data]);
  const save = useMutation({
    mutationFn: async () => {
      for (const m of q.data?.months ?? []) {
        const v = draft[m.month] ?? '';
        const next = v.trim() === '' ? null : Number(v);
        if (next !== m.amount) {
          await finance.setRevenuePlan({ organization_id: orgId, year, month: m.month, amount: next });
        }
      }
    },
    onSuccess: () => { setErr(null); qc.invalidateQueries({ queryKey: ['finance'] }); qc.invalidateQueries({ queryKey: ['finance-plan'] }); onClose(); },
    onError: (e) => setErr(extractApiError(e)),
  });
  const total = Object.values(draft).reduce((s, v) => s + (Number(v) || 0), 0);
  return (
    <BodyPortal>
      <div style={{ position: 'fixed', inset: 0, zIndex: 1100, background: 'var(--overlay)', display: 'flex',
        alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
        <div className="card" role="dialog" aria-label={`План виручки на ${year} рік`}
          style={{ width: 520, maxWidth: '94vw', maxHeight: '85vh', overflowY: 'auto', margin: 0 }}
          onClick={(e) => e.stopPropagation()}>
          <div className="toolbar" style={{ marginBottom: '0.5rem' }}>
            <h3 style={{ margin: 0 }}>План виручки на {year} рік</h3>
            <button type="button" style={{ marginLeft: 'auto' }} onClick={onClose}>закрити</button>
          </div>
          <p style={{ marginTop: 0, color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
            Одна сума на місяць, грн. Порожнє поле — плану на місяць немає.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem' }}>
            {(q.data?.months ?? []).map((m) => (
              <div className="field" key={m.month} style={{ marginBottom: 0 }}>
                <label htmlFor={`fin-plan-${m.month}`}>{MONTHS_UK[m.month]}</label>
                <input id={`fin-plan-${m.month}`} type="number" min={0} step="0.01" value={draft[m.month] ?? ''}
                  onChange={(e) => setDraft({ ...draft, [m.month]: e.target.value })} />
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '0.75rem' }}>
            <span style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>Разом за рік: {fmt0(total)} грн</span>
            <button type="button" className="primary" style={{ marginLeft: 'auto' }} disabled={save.isPending || q.isLoading}
              onClick={() => save.mutate()}>зберегти</button>
          </div>
          {err && <div className="error">{err}</div>}
        </div>
      </div>
    </BodyPortal>
  );
}

export default function FinancePage() {
  const qc = useQueryClient();
  const perms = usePermissions();
  const canEditPlan = perms.has('reports.management.edit');
  const { availableOrgs, loading: orgsLoading, hasPermissionInAnyOrg } = usePermissionsByOrg('reports.management.view');
  const today = new Date();
  const [preset, setPreset] = useStickyState<Preset>('finance.preset', 'month');
  const [year, setYear] = useStickyState<number>('finance.year', today.getFullYear());
  const [month, setMonth] = useStickyState<number>('finance.month', today.getMonth() + 1);
  const [quarter, setQuarter] = useStickyState<number>('finance.quarter', quarterOf(today.getMonth() + 1));
  const [from, setFrom] = useStickyState<string>('finance.from', monthBounds(today.getFullYear(), today.getMonth() + 1).period_start);
  const [to, setTo] = useStickyState<string>('finance.to', today.toISOString().slice(0, 10));
  const [orgId, setOrgId] = useStickyState<string>('finance.org', '');
  const [drill, setDrill] = useState<DrillTarget | null>(null);
  const [planEdit, setPlanEdit] = useState(false);
  const [yearPlan, setYearPlan] = useState(false);

  // Організація зі списку доступних; порожньо — усі. Збережену, якої вже немає, скидаємо.
  useEffect(() => {
    if (orgId && !orgsLoading && availableOrgs.length && !availableOrgs.some((o) => o.id === orgId)) setOrgId('');
  }, [orgId, orgsLoading, availableOrgs, setOrgId]);

  const bounds = useMemo(() => {
    if (preset === 'month') return monthBounds(year, month);
    if (preset === 'quarter') return quarterBounds(year, quarter);
    if (preset === 'year') return { period_start: `${year}-01-01`, period_end: `${year}-12-31` };
    return { period_start: from, period_end: to };
  }, [preset, year, month, quarter, from, to]);
  const scope = useMemo(() => ({ ...bounds, organization_id: orgId || undefined }), [bounds, orgId]);

  const q = useQuery({
    queryKey: ['finance', scope],
    queryFn: () => finance.summary(scope),
    enabled: !orgsLoading && hasPermissionInAnyOrg && !!bounds.period_start && !!bounds.period_end,
  });
  const d: FinSummary | undefined = q.data;
  const refresh = () => { qc.invalidateQueries({ queryKey: ['finance'] }); setPlanEdit(false); };
  const years = useMemo(() => {
    const y = today.getFullYear();
    return [y + 1, y, y - 1, y - 2, y - 3];
  }, [today]);

  if (!orgsLoading && !hasPermissionInAnyOrg) {
    return (
      <div className="card">
        <h2 style={{ marginTop: 0 }}>Фінанси</h2>
        <p>У жодній організації немає права на управлінські звіти. Зверніться до адміністратора акаунта.</p>
      </div>
    );
  }

  const p = d?.profit;
  const plan = d?.plan;
  const planMonth = plan?.editable_month ?? null;

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Фінанси</h2>
        <button type="button" onClick={() => window.print()}>друк</button>
      </div>
      <p style={{ marginTop: 0, color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
        Скільки заробили, куди пішли гроші, хто кому винен — за період і проти попереднього. Натисніть на
        рядок, щоб побачити документи. Нічого налаштовувати не треба: категорії беруться з документів.
      </p>

      <div className="doc-grid" style={{ marginBottom: '0.75rem' }}>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label htmlFor="fin-org">Організація</label>
          <select id="fin-org" value={orgId} onChange={(e) => setOrgId(e.target.value)}>
            <option value="">Усі організації ({availableOrgs.length})</option>
            {availableOrgs.map((o) => <option key={o.id} value={o.id}>{o.name} — {o.code}</option>)}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 2' }}>
          <label htmlFor="fin-preset">Період</label>
          <select id="fin-preset" value={preset} onChange={(e) => setPreset(e.target.value as Preset)}>
            <option value="month">Місяць</option>
            <option value="quarter">Квартал</option>
            <option value="year">Рік</option>
            <option value="custom">Довільний</option>
          </select>
        </div>
        {preset === 'month' && (
          <div className="field" style={{ gridColumn: 'span 3' }}>
            <label htmlFor="fin-month">Місяць</label>
            <select id="fin-month" value={month} onChange={(e) => setMonth(Number(e.target.value))}>
              {MONTHS_UK.slice(1).map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>
          </div>
        )}
        {preset === 'quarter' && (
          <div className="field" style={{ gridColumn: 'span 3' }}>
            <label htmlFor="fin-quarter">Квартал</label>
            <select id="fin-quarter" value={quarter} onChange={(e) => setQuarter(Number(e.target.value))}>
              {['I', 'II', 'III', 'IV'].map((qq, i) => <option key={qq} value={i + 1}>{qq} квартал</option>)}
            </select>
          </div>
        )}
        {preset !== 'custom' && (
          <div className="field" style={{ gridColumn: 'span 3' }}>
            <label htmlFor="fin-year">Рік</label>
            <select id="fin-year" value={year} onChange={(e) => setYear(Number(e.target.value))}>
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        )}
        {preset === 'custom' && (
          <>
            <div className="field" style={{ gridColumn: 'span 3' }}>
              <label>З</label>
              <DatePopover value={from} onChange={setFrom} />
            </div>
            <div className="field" style={{ gridColumn: 'span 3' }}>
              <label>По</label>
              <DatePopover value={to} onChange={setTo} />
            </div>
          </>
        )}
      </div>

      {q.isLoading && <p>Рахуємо…</p>}
      {q.isError && <div className="error">{extractApiError(q.error)}</div>}
      {d && !p && <p style={{ color: 'var(--muted)' }}>Немає організацій для зведення.</p>}

      {d && p && d.cash && d.debts && (
        <>
          <div style={{ color: 'var(--muted)', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
            {d.period.label} · проти: {d.previous.label}
            {d.organizations.length > 1 && <> · агреговано по {d.organizations.length} організаціях</>}
          </div>
          {d.warnings.map((w) => (
            <div key={w} role="note" style={{ background: 'var(--badge-amber-bg)', color: 'var(--badge-amber-fg)',
              border: '1px solid var(--badge-amber-bd)', borderRadius: 'var(--radius-sm)', padding: '0.4rem 0.6rem',
              fontSize: '0.85rem', marginBottom: '0.5rem' }}>{w}</div>
          ))}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '0.6rem',
            marginBottom: '1rem' }}>
            <Kpi label="Виручка" value={fmt0(p.revenue.total)}
              sub={<>
                <Delta now={p.revenue.total} prev={p.revenue.prev} />
                {plan?.available && plan.amount != null && (
                  <span style={{ marginLeft: 8, color: 'var(--muted)' }}>
                    план {fmt0(plan.amount)}{plan.progress_pct != null && <> · <b>{Math.round(plan.progress_pct)}%</b></>}
                  </span>
                )}
              </>} />
            <Kpi label="Прибуток" value={fmt0(p.profit)} tone={p.profit < 0 ? 'bad' : 'good'}
              sub={<><Delta now={p.profit} prev={p.profit_prev} />
                {p.profit_margin_pct != null && <span style={{ marginLeft: 8, color: 'var(--muted)' }}>рентабельність {p.profit_margin_pct}%</span>}</>} />
            <Kpi label="Гроші на кінець" value={fmt0(d.cash.closing.total)}
              sub={<span style={{ color: 'var(--muted)' }}>банк {fmt0(d.cash.closing.bank)} · каса {fmt0(d.cash.closing.cash)}</span>} />
            <Kpi label="Винні нам" value={fmt0(d.debts.receivable.total)}
              sub={<span style={{ color: d.debts.receivable.overdue > 0 ? 'var(--badge-red-fg)' : 'var(--muted)' }}>
                прострочено {fmt0(d.debts.receivable.overdue)}</span>} />
            <Kpi label="Винні ми" value={fmt0(d.debts.payable.total)}
              sub={<span style={{ color: d.debts.payable.overdue > 0 ? 'var(--badge-red-fg)' : 'var(--muted)' }}>
                прострочено {fmt0(d.debts.payable.overdue)}</span>} />
          </div>

          {/* План виручки */}
          <div style={{ marginBottom: '1rem', fontSize: '0.9rem' }}>
            {plan && !plan.available && <span style={{ color: 'var(--muted)' }}>{plan.reason}</span>}
            {plan?.available && plan.amount == null && (
              <span style={{ color: 'var(--muted)' }}>План виручки на цей період не задано.</span>
            )}
            {plan?.available && plan.amount != null && plan.months > 1 && (
              <span style={{ color: 'var(--muted)' }}>
                План виручки: {fmt0(plan.amount)} грн (задано на {plan.planned_months} з {plan.months} місяців).
              </span>
            )}
            {canEditPlan && orgId && planMonth && !planEdit && (
              <button type="button" className="btn-link" style={{ marginLeft: 8 }} onClick={() => setPlanEdit(true)}>
                {plan?.amount != null ? 'змінити план місяця' : 'задати план на місяць'}
              </button>
            )}
            {canEditPlan && orgId && preset !== 'custom' && (
              <button type="button" className="btn-link" style={{ marginLeft: 8 }} onClick={() => setYearPlan(true)}>
                плани на {year} рік
              </button>
            )}
            {canEditPlan && !orgId && plan?.available && (
              <span style={{ marginLeft: 8, color: 'var(--muted)' }}>Щоб задати план, оберіть одну організацію.</span>
            )}
            {planEdit && orgId && planMonth && (
              <PlanEditor orgId={orgId} year={planMonth.year} month={planMonth.month}
                current={plan?.amount ?? null} onSaved={refresh} />
            )}
          </div>

          <h3 style={{ margin: '0.5rem 0' }}>1. Прибуток</h3>
          <TchTable style={{ width: '100%' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Стаття</th>
                <th style={{ width: 150, textAlign: 'right' }}>{d.period.label}</th>
                <th style={{ width: 150, textAlign: 'right' }}>{d.previous.label}</th>
                <th style={{ width: 110 }}>зміна</th>
              </tr>
            </thead>
            <tbody>
              <LinesTable title="Виручка" block={p.revenue} metric="revenue" onDrill={setDrill}
                emptyText="Продажів за період немає" />
              <LinesTable title="Собівартість проданого" block={p.cogs} metric="cogs" invert onDrill={setDrill}
                emptyText="—" />
              <tr style={{ fontWeight: 700 }}>
                <td>Валовий прибуток{p.gross_margin_pct != null && <span style={{ color: 'var(--muted)', fontWeight: 400 }}> · {p.gross_margin_pct}%</span>}</td>
                <td style={{ textAlign: 'right' }}>{fmt(p.gross_profit)}</td>
                <td style={{ textAlign: 'right', color: 'var(--muted)' }}>{fmt(p.gross_profit_prev)}</td>
                <td><Delta now={p.gross_profit} prev={p.gross_profit_prev} /></td>
              </tr>
              <LinesTable title="Витрати" block={p.expenses} metric="expense" invert onDrill={setDrill}
                emptyText="Витрат за період немає" />
              <LinesTable title="Інші доходи" block={p.other_income} metric="other_income" onDrill={setDrill}
                emptyText="—" />
            </tbody>
            <tfoot>
              <tr style={{ fontWeight: 700, background: 'var(--primary-soft)' }}>
                <td>Прибуток{p.profit_margin_pct != null && <span style={{ color: 'var(--muted)', fontWeight: 400 }}> · {p.profit_margin_pct}% від виручки</span>}</td>
                <td style={{ textAlign: 'right' }}>{fmt(p.profit)}</td>
                <td style={{ textAlign: 'right' }}>{fmt(p.profit_prev)}</td>
                <td><Delta now={p.profit} prev={p.profit_prev} /></td>
              </tr>
            </tfoot>
          </TchTable>

          <h3 style={{ margin: '1rem 0 0.5rem' }}>2. Гроші</h3>
          <TchTable style={{ width: '100%' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Стаття</th>
                <th style={{ width: 150, textAlign: 'right' }}>{d.period.label}</th>
                <th style={{ width: 150, textAlign: 'right' }}>{d.previous.label}</th>
                <th style={{ width: 110 }}>зміна</th>
              </tr>
            </thead>
            <tbody>
              <tr style={{ fontWeight: 600 }}>
                <td>На початок періоду</td>
                <td style={{ textAlign: 'right' }}>{fmt(d.cash.opening.total)}</td>
                <td colSpan={2} style={{ color: 'var(--muted)' }}>банк {fmt(d.cash.opening.bank)} · каса {fmt(d.cash.opening.cash)}</td>
              </tr>
              <LinesTable title="Надходження" block={d.cash.inflow} metric="cash_in" onDrill={setDrill}
                emptyText="Надходжень не було" />
              <LinesTable title="Витрати" block={d.cash.outflow} metric="cash_out" invert onDrill={setDrill}
                emptyText="Витрат не було" />
              <tr style={{ fontWeight: 600 }}>
                <td>Чистий рух</td>
                <td style={{ textAlign: 'right' }}>{fmt(d.cash.net)}</td>
                <td style={{ textAlign: 'right', color: 'var(--muted)' }}>{fmt(d.cash.net_prev)}</td>
                <td><Delta now={d.cash.net} prev={d.cash.net_prev} /></td>
              </tr>
            </tbody>
            <tfoot>
              <tr style={{ fontWeight: 700, background: 'var(--primary-soft)' }}>
                <td>На кінець періоду</td>
                <td style={{ textAlign: 'right' }}>{fmt(d.cash.closing.total)}</td>
                <td colSpan={2}>банк {fmt(d.cash.closing.bank)} · каса {fmt(d.cash.closing.cash)}</td>
              </tr>
            </tfoot>
          </TchTable>
          {d.cash.internal_transfers > 0 && (
            <div style={{ color: 'var(--muted)', fontSize: '0.85rem', marginTop: 4 }}>
              Перекази між своїми рахунками й касою ({fmt(d.cash.internal_transfers)} грн) не є надходженням чи витратою — не враховані.
            </div>
          )}

          <h3 style={{ margin: '1rem 0 0.5rem' }}>3. Борги на сьогодні</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1rem' }}>
            {(['receivable', 'payable'] as const).map((side) => {
              const s = d.debts![side];
              const links = debtReportLinks(side, orgId || undefined);
              return (
                <div key={side}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
                    <b>{side === 'receivable' ? 'Винні нам' : 'Винні ми'}</b>
                    <span>{fmt(s.total)} грн · {s.count} контрагент(ів)</span>
                    <Link to={links.all} style={{ marginLeft: 'auto', fontSize: '0.85rem' }}>усі →</Link>
                  </div>
                  {s.overdue > 0 ? (
                    <Link to={links.overdue} style={{ color: 'var(--badge-red-fg)', fontSize: '0.85rem' }}>
                      прострочено за накладними: {fmt(s.overdue)} грн · {s.overdue_docs} док.
                    </Link>
                  ) : <span style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>прострочених накладних немає</span>}
                  <table className="tch" style={{ width: '100%', marginTop: 4 }}>
                    <tbody>
                      {s.top.map((t) => (
                        <tr key={t.contractor_id}>
                          <td><Link to={`/contractors/${t.contractor_id}`}>{t.name}</Link></td>
                          <td style={{ textAlign: 'right', width: 140 }}>{fmt(t.amount)}</td>
                        </tr>
                      ))}
                      {s.top.length === 0 && <tr><td style={{ color: 'var(--muted)' }}>Боргів немає</td></tr>}
                    </tbody>
                  </table>
                </div>
              );
            })}
          </div>
        </>
      )}

      {drill && <DrillModal target={drill} scope={scope} onClose={() => setDrill(null)} />}
      {yearPlan && orgId && <YearPlanModal orgId={orgId} year={year} onClose={() => { setYearPlan(false); refresh(); }} />}
    </div>
  );
}
